/**
 * 列のサブツリー内の位置を表すパス。
 *
 * 固定行セルは列の複製で、常に同じ形を保つ。そのため列側のあるノードに対応する
 * 固定行側のノードは、列を起点にした位置の並びだけで特定できる。
 *
 * パスは質問と条件分岐を交互に降りる。
 * 偶数番目 = 質問が持つ条件分岐の位置、奇数番目 = 条件分岐が持つ分岐質問の位置。
 *
 *   列
 *   └─ 条件分岐0            パス [0]
 *       └─ 分岐質問1        パス [0, 1]
 *           └─ 条件分岐0    パス [0, 1, 0]
 *
 * MEMO: 文章だけでつかみにくければ、AI に図解させてみてください。かなりわかります。
 */

import { TemplateNodeSchema } from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import { getLogicBranchQuestions, getQuestionLogicNodes } from '../selector'

/** 列を起点にした位置の並び。空配列は列そのもの。 */
export type SubtreePath = readonly number[]

/** パスの深さに応じて、その位置で選ぶ子ノードを返す。 */
const childrenAt = (
  node: TemplateNodeSchema,
  depth: number,
  templateNodes: TemplateNodesDict,
): TemplateNodeSchema[] =>
  depth % 2 === 0
    ? getQuestionLogicNodes(node, templateNodes)
    : getLogicBranchQuestions(node, templateNodes)

/**
 * 列のサブツリーから、目的のノードまでのパスを探す。見つからなければ undefined。
 * 列そのものを指すときは空配列を返す。
 */
export const findSubtreePath = (
  columnNode: TemplateNodeSchema,
  targetNodeId: number,
  templateNodes: TemplateNodesDict,
): SubtreePath | undefined => {
  const search = (
    node: TemplateNodeSchema,
    path: number[],
  ): SubtreePath | undefined => {
    if (node.id === targetNodeId) return path
    const children = childrenAt(node, path.length, templateNodes)
    for (const [index, child] of children.entries()) {
      const found = search(child, [...path, index])
      if (found) return found
    }
    return undefined
  }
  return search(columnNode, [])
}

/**
 * 固定行セルのサブツリーをパスどおりにたどる。
 * 列と形が一致していなければ途中で子が足りなくなるので undefined を返す。
 */
export const resolveSubtreePath = (
  cellNode: TemplateNodeSchema,
  path: SubtreePath,
  templateNodes: TemplateNodesDict,
): TemplateNodeSchema | undefined => {
  let node: TemplateNodeSchema | undefined = cellNode
  for (const [depth, index] of path.entries()) {
    if (!node) return undefined
    node = childrenAt(node, depth, templateNodes)[index]
  }
  return node
}
